import { FadeUp } from "../components/Animations";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const SERVICE_IMG = "https://customer-assets.emergentagent.com/job_elevated-spaces-1/artifacts/biszzgyq_shutterstock_2702838913.jpg";

const SERVICES = [
  {
    num: "01",
    title: "Interior Design",
    description:
      "Fully customized residential and commercial design. Space planning, material selection, furniture sourcing, lighting, and decor — every detail curated to your vision.",
    steps: [
      "Discovery & Consultation",
      "Concept Development",
      "Design Selections & Sourcing",
      "Installation & Styling",
    ],
  },
  {
    num: "02",
    title: "Project Management",
    description:
      "End-to-end oversight of your build or renovation. Budget management, contractor coordination, timeline control, and quality assurance from start to finish.",
    steps: [
      "Scope & Budget Planning",
      "Contractor Selection",
      "Progress Oversight",
      "Final Walkthrough",
    ],
  },
  {
    num: "03",
    title: "Full-Service Design",
    description:
      "The complete experience. From initial concept through construction and final styling — a seamless, hands-on process that delivers spaces beyond expectation.",
    steps: [
      "Vision & Strategy",
      "Design & Documentation",
      "Build & Construction",
      "Furnishing & Reveal",
    ],
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="pt-20">
      {/* ── Hero ── */}
      <section className="py-20 md:py-28 px-6 md:px-16 max-w-[1400px] mx-auto">
        <FadeUp>
          <p className="text-xs tracking-[0.3em] uppercase text-taupe mb-8">
            Services
          </p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light text-onyx leading-[1.05] max-w-4xl">
            Interiors as unique as you are.
          </h1>
        </FadeUp>
      </section>

      {/* ── Image Banner ── */}
      <section className="px-6 md:px-16 max-w-[1400px] mx-auto mb-24 md:mb-32">
        <FadeUp>
          <div className="img-scale aspect-[21/9] w-full">
            <img
              src={SERVICE_IMG}
              alt="Marble texture"
              className="w-full h-full object-cover"
            />
          </div>
        </FadeUp>
      </section>

      {/* ── Services Detail ── */}
      {SERVICES.map((service, i) => (
        <section
          key={service.num}
          data-testid={`service-section-${i}`}
          className={`py-20 md:py-28 px-6 md:px-16 ${
            i % 2 === 1 ? "bg-onyx" : ""
          }`}
        >
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
            <div className="md:col-span-2">
              <FadeUp>
                <span
                  className={`font-serif text-6xl font-light ${
                    i % 2 === 1 ? "text-bone/20" : "text-concrete"
                  }`}
                >
                  {service.num}
                </span>
              </FadeUp>
            </div>

            <div className="md:col-span-5">
              <FadeUp delay={0.1}>
                <h2
                  className={`font-serif text-3xl md:text-4xl font-light mb-6 ${
                    i % 2 === 1 ? "text-bone" : "text-onyx"
                  }`}
                >
                  {service.title}
                </h2>
                <p
                  className={`text-base font-light leading-[1.85] ${
                    i % 2 === 1 ? "text-concrete" : "text-onyx/70"
                  }`}
                >
                  {service.description}
                </p>
              </FadeUp>
            </div>

            <div className="md:col-span-5">
              <FadeUp delay={0.25}>
                <p
                  className={`text-xs tracking-[0.25em] uppercase mb-6 ${
                    i % 2 === 1 ? "text-taupe" : "text-taupe"
                  }`}
                >
                  Process
                </p>
                <div className="flex flex-col gap-4">
                  {service.steps.map((step, j) => (
                    <div
                      key={j}
                      className={`flex items-center gap-4 pb-4 border-b ${
                        i % 2 === 1 ? "border-bone/10" : "border-concrete"
                      }`}
                    >
                      <span
                        className={`text-xs font-light ${
                          i % 2 === 1 ? "text-taupe" : "text-taupe"
                        }`}
                      >
                        {String(j + 1).padStart(2, "0")}
                      </span>
                      <span
                        className={`text-sm font-light ${
                          i % 2 === 1 ? "text-bone" : "text-onyx"
                        }`}
                      >
                        {step}
                      </span>
                    </div>
                  ))}
                </div>
              </FadeUp>
            </div>
          </div>
        </section>
      ))}

      {/* ── What Sets Us Apart ── */}
      <section className="py-24 md:py-32 px-6 md:px-16 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1566041510394-cf7c8fe21800?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHx3aGl0ZSUyMG1hcmJsZSUyMHRleHR1cmUlMjBiYWNrZ3JvdW5kJTIwbWluaW1hbHxlbnwwfHx8fDE3NzYyMDA5MTN8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 max-w-[1400px] mx-auto">
          <FadeUp>
            <p className="text-xs tracking-[0.3em] uppercase text-taupe mb-8">
              Why J Design Haus
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {[
              {
                title: "Fully Customized",
                desc: "No templates. No packages. Every design is built around your life, your taste, your goals.",
              },
              {
                title: "Expert Management",
                desc: "Project management from day one. Budgets, timelines, and contractors — all handled.",
              },
              {
                title: "Personal & Hands-On",
                desc: "Direct access to your designer. Every detail is intentional. Every decision, collaborative.",
              },
            ].map((item, i) => (
              <FadeUp key={i} delay={i * 0.15}>
                <div data-testid={`differentiator-${i}`}>
                  <h3 className="font-serif text-2xl font-light text-onyx mb-4">
                    {item.title}
                  </h3>
                  <p className="text-sm font-light text-onyx/70 leading-[1.85]">
                    {item.desc}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 md:py-32 px-6 md:px-16 max-w-[1400px] mx-auto text-center">
        <FadeUp>
          <h2 className="font-serif text-4xl md:text-6xl font-light text-onyx mb-10">
            Start Your Project
          </h2>
        </FadeUp>
        <FadeUp delay={0.2}>
          <Link
            to="/contact"
            data-testid="services-contact-cta"
            className="inline-block bg-onyx text-bone px-10 py-4 text-sm tracking-[0.2em] uppercase font-light hover:bg-taupe transition-colors duration-500"
          >
            Get in Touch
          </Link>
        </FadeUp>
      </section>
    </div>
  );
}
